import { mapGetters } from 'vuex'

import { shortPath } from 'app/releases'
import { marketing } from 'app/concerns'
import { determinations } from 'app/utils/accounts'
import { downloadBlockReason, requiresCounterpartLookup } from 'app/views/plans/upsellPolicy'
import get from 'lodash/get'
import merge from 'lodash/merge'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'
import moment from 'moment'
import snakeCase from 'lodash/snakeCase'

export const versionProvider = {
  provide() {
    return {
      version: this.version,
      urlPrefix: this.urlPrefix
    }
  },

  computed: {
    version() {
      return this.$route.meta.version
    },

    urlPrefix() {
      return `/${shortPath(this.$releases.active.version)}`
    }
  }
}

export const adFreeDocumentationPermitted = {
  computed: {
    ...mapGetters('permits', ['adFreeDocumentationVersions']),

    adFreeDocumentationPermitted() {
      return !!this.adFreeDocumentationVersions.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const hasAccessToProForActiveVersion = {
  computed: {
    ...mapGetters('permits', ['accessToPro']),

    hasAccessToProForActiveVersion() {
      return !!this.accessToPro.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const hasAccessToProPlusForActiveVersion = {
  computed: {
    ...mapGetters('permits', ['accessToProPlus']),

    hasAccessToProPlusForActiveVersion() {
      return !!this.accessToProPlus.find(({ major, minor, patch, pre }) => {
        return isEqual({ major, minor, patch, pre }, this.$releases.active.versionParts)
      })
    }
  }
}

export const SubscriptionMixin = {
  computed: {
    ...mapGetters('accounts', ['currentAccount']),

    ...mapGetters('subscriptions', ['currentSubscription']),

    marketing() {
      return marketing({ account: this.currentAccount, subscription: this.currentSubscription })
    },

    proLocked() {
      return !this.$dt.hasActivePaidSubscription
    }
  }
}

export const EventTrackingMixin = {
  computed: {
    // Entitlement-aware, not the raw record (see ModalUpsell.entitledPlan),
    // so the event and the message can never disagree about a customer.
    trackedPlan() {
      return this.$dt.hasActivePaidSubscription ? get(this.$account, ['subscription', 'plan'], null) : null
    },

    determinationsEventProps() {
      const dt = determinations({ account: this.$account })
      const plan = this.trackedPlan

      return {
        has_subscription: dt.hasSubscription,
        has_active_subscription: dt.hasActiveSubscription,
        has_canceled_subscription: dt.hasCanceledSubscription,
        has_free_subscription: dt.hasFreeSubscription,
        has_pro_subscription: dt.hasProSubscription,
        is_free: dt.isFree,
        is_backer: dt.isBacker,
        is_signed_in: this.$route.meta.isSignedIn,
        // Explicit nulls rather than omitted keys — a query needs to tell
        // "no plan" apart from "property not deployed yet".
        plan_sku: plan ? plan.sku : null,
        plan_annual: plan ? !!plan.annual : null,
        download_block_reason: downloadBlockReason({
          currentPlan: plan,
          getPlan: this.$store?.getters?.['plans/getPlan']
        })
      }
    }
  },

  methods: {
    nameSlug(name) {
      return snakeCase(name)
    },

    // `download_block_reason` needs the plans store, which loads lazily — a
    // gate reached before any plan view would report every monthly customer
    // as Cloud. Fetch only for the population whose answer depends on it,
    // and never let a failed fetch drop the capture.
    async purchaseFlowEvent(action, params = {}) {
      if (requiresCounterpartLookup(this.trackedPlan)) {
        try {
          await this.$store.dispatch('plans/ensurePlansLoaded')
        } catch (error) {
          // Fall through and capture with whatever the store already has.
        }
      }

      this.$posthog.capture(`purchase_flow:${action}`, merge(params, this.determinationsEventProps))
    },

    iconModalContext(isUsingModal) {
      return isUsingModal ? 'icon_modal' : 'icon_landing'
    },

    supportLinkClicked(context, location = null) {
      this.$posthog.capture('contact_support_link_click', {
        context: context,
        location: location
      })
    },

    fontawesomeDownloadEvent(type, medium, version, context = null, location = null) {
      this.$posthog.capture(
        `download:fontawesome_download_button_click`,
        merge({ type: type, medium: medium, version: version, context: context }, this.determinationsEventProps)
      )
    }
  }
}

export const CountdownMixin = {
  methods: {
    timeRemaining(endDate) {
      moment.updateLocale('en', {
        relativeTime: {
          d: '1 day',
          h: '1 hour',
          m: '1 minute',
          M: '1 month',
          y: '1 year'
        }
      })

      const humanReadable = moment.parseZone(endDate).fromNow(true)

      moment.updateLocale('en', null)

      return humanReadable
    },

    isInFuture(endDate) {
      return moment.utc().isBefore(moment.parseZone(endDate))
    }
  }
}

export const StickyElementMixin = {
  data() {
    return {
      isStickyElementStuck: false
    }
  },

  async updated() {
    this.$nextTick(this.setStickyElementParentRelative)
    this.$nextTick(this.setupStickyObserver)
  },

  methods: {
    setStickyElementParentRelative() {
      const parent = this.$refs.StickyElementParent

      if (parent) {
        parent.style.position = 'relative'
      }
    },

    setupStickyObserver() {
      const sentinel = this.$refs.StickyElementSentinel

      if (sentinel) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            this.isStickyElementStuck = !entry.isIntersecting
          },
          {
            root: null,
            threshold: 0
          }
        )

        observer.observe(sentinel)
      }
    }
  }
}

// Version-specific docs prefix patterns
const VERSION_5_DOCS_PREFIX = /^\/v5\/docs/
const VERSION_6_DOCS_PREFIX = /^\/v6\/docs/
const DEFAULT_DOCS_PREFIX = /^\/docs/

export const DocsMixin = {
  methods: {
    getDocsPath(pathOrLink, options = {}) {
      const currentVersion = options.version
      const isLatestVersion = currentVersion === this.$releases.latest.together.versionParts.major
      const docsVersionPath = isLatestVersion ? this.$docs : this.$docs[`v${currentVersion}`]

      const path = typeof pathOrLink === 'object' ? pathOrLink.path : pathOrLink
      const params = typeof pathOrLink === 'object' ? { hash: pathOrLink.hash } : undefined

      // Remove version-specific docs prefix if present
      let cleanPath = path
      if (currentVersion === 5) {
        cleanPath = path.replace(VERSION_5_DOCS_PREFIX, '')
      } else if (currentVersion === 6) {
        cleanPath = path.replace(VERSION_6_DOCS_PREFIX, '')
      } else {
        cleanPath = path.replace(DEFAULT_DOCS_PREFIX, '')
      }

      // Ensure path starts with a slash for proper router resolution
      if (!cleanPath.startsWith('/')) {
        cleanPath = '/' + cleanPath
      }

      return params?.hash ? docsVersionPath(cleanPath, params) : docsVersionPath(cleanPath)
    }
  }
}

export const RouteValidationMixin = {
  methods: {
    /**
     * Validates that a route object has the expected structure for use with router.resolve()
     * @param {Object} route - The route object to validate
     * @returns {boolean} - True if the route is valid, false otherwise
     */
    validateRoute(route) {
      if (!route) return false

      return (
        isObject(route) &&
        typeof route.name === 'string' &&
        (route.params === undefined || isObject(route.params)) &&
        (route.query === undefined || isObject(route.query))
      )
    }
  }
}

export const UpgradeRouteMixin = {
  methods: {
    getUpgradeRoute(requireProPlus) {
      const currentAccountPlanSku = this.$account?.subscription?.plan?.sku

      if (requireProPlus && currentAccountPlanSku === 'FA-MAX') {
        return 'plans.pro-max-plus'
      } else if (requireProPlus) {
        return 'plans.pro-plus'
      } else {
        return 'plans.pro'
      }
    }
  }
}

// Date window for the new pack notice on pages that use NewPackNoticeMixin
// Account, Download, Home, IconsLanding, IconLanding, Plans, Support, Search, Start
// The notice shows between START and END (inclusive of START, exclusive of END),
// then hides itself automatically. Set END to null to keep it on with no expiry.
// Other views can be added by binding is-showing-new-pack-notice in the view.
const NEW_PACK_NOTICE_START = '2026-06-22'
const NEW_PACK_NOTICE_END = '2026-07-13'

export const NewPackNoticeMixin = {
  computed: {
    isShowingNewPackNotice() {
      const now = moment.utc()
      const afterStart = now.isSameOrAfter(moment.utc(NEW_PACK_NOTICE_START))
      const beforeEnd = NEW_PACK_NOTICE_END === null || now.isBefore(moment.utc(NEW_PACK_NOTICE_END))

      return afterStart && beforeEnd
    }
  }
}
